import Footer from "@/components/Footer";
import LandingNav from "@/components/LandingNav";
import { AppHeader } from "@/components/AppHeader";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import SEOHead from "@/components/SEOHead";
import { useTranslation } from "react-i18next";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="min-h-screen bg-background flex flex-col w-full">
        <SEOHead
          title="Privacy Policy – TestAssets"
          description="TestAssets privacy policy. Learn how we handle your data, cookies, and personal information."
          path="/privacy-policy"
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "Privacy Policy", url: "/privacy-policy" },
          ]}
        />
        {user ? <AppHeader /> : <LandingNav />}

      <main className="flex-1 container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-foreground">{t("privacy.title")}</h1>
            <p className="text-muted-foreground">{t("privacy.lastUpdated")}</p>
          </div>

          <div className="space-y-8">
            {/* 1. Data Controller */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.responsible.title")}</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>{t("privacy.responsible.description")}</p>
                <div className="mt-4">
                  <p className="font-medium text-foreground">Christoph Schwenk</p>
                  <p>c/o flexdienst – #12272</p>
                  <p>Kurt-Schumacher-Straße 76</p>
                  <p>67663 Kaiserslautern</p>
                  <p>{t("privacy.responsible.country")}</p>
                </div>
                <p className="mt-4">
                  <strong className="text-foreground">{t("privacy.responsible.email")}:</strong>{" "}
                  <a href="mailto:support@testassets.io" className="text-primary hover:underline">support@testassets.io</a>
                </p>
              </div>
            </section>

            {/* 2. Data Collection */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.dataCollection.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <h3 className="text-lg font-medium text-foreground">{t("privacy.dataCollection.websiteVisit.title")}</h3>
                <p>{t("privacy.dataCollection.websiteVisit.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.dataCollection.websiteVisit.items.ip")}</li>
                  <li>{t("privacy.dataCollection.websiteVisit.items.datetime")}</li>
                  <li>{t("privacy.dataCollection.websiteVisit.items.file")}</li>
                  <li>{t("privacy.dataCollection.websiteVisit.items.referrer")}</li>
                  <li>{t("privacy.dataCollection.websiteVisit.items.browser")}</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mt-6">{t("privacy.dataCollection.registration.title")}</h3>
                <p>{t("privacy.dataCollection.registration.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.dataCollection.registration.items.email")}</li>
                  <li>{t("privacy.dataCollection.registration.items.name")}</li>
                  <li>{t("privacy.dataCollection.registration.items.organization")}</li>
                  <li>{t("privacy.dataCollection.registration.items.testData")}</li>
                  <li>{t("privacy.dataCollection.registration.items.marketing")}</li>
                </ul>
              </div>
            </section>

            {/* 3. Purpose */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.purpose.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.purpose.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.purpose.items.website")}</li>
                  <li>{t("privacy.purpose.items.service")}</li>
                  <li>{t("privacy.purpose.items.contact")}</li>
                  <li>{t("privacy.purpose.items.security")}</li>
                  <li>{t("privacy.purpose.items.marketingPurpose")}</li>
                </ul>
              </div>
            </section>

            {/* 4. Legal Basis */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.legalBasis.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.legalBasis.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>{t("privacy.legalBasis.items.consent.ref")}</strong> {t("privacy.legalBasis.items.consent.description")}</li>
                  <li><strong>{t("privacy.legalBasis.items.contract.ref")}</strong> {t("privacy.legalBasis.items.contract.description")}</li>
                  <li><strong>{t("privacy.legalBasis.items.interest.ref")}</strong> {t("privacy.legalBasis.items.interest.description")}</li>
                </ul>
              </div>
            </section>

            {/* 5. Data Sharing */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.dataSharing.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.dataSharing.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.dataSharing.items.consent")}</li>
                  <li>{t("privacy.dataSharing.items.legal")}</li>
                  <li>{t("privacy.dataSharing.items.rights")}</li>
                  <li>{t("privacy.dataSharing.items.processors")}</li>
                </ul>
              </div>
            </section>

            {/* 6. Sub-processors */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.subProcessors.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.subProcessors.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.subProcessors.items.hosting")}</li>
                  <li>{t("privacy.subProcessors.items.auth")}</li>
                  <li>{t("privacy.subProcessors.items.email")}</li>
                </ul>
                <p className="mt-4 text-sm">{t("privacy.subProcessors.note")}</p>
              </div>
            </section>

            {/* 7. Cookies and Local Storage */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.cookies.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.cookies.intro")}</p>

                <h3 className="text-lg font-medium text-foreground mt-6">{t("privacy.cookies.essential.title")}</h3>
                <p>{t("privacy.cookies.essential.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.cookies.essential.items.session")}</li>
                  <li>{t("privacy.cookies.essential.items.consent")}</li>
                  <li>{t("privacy.cookies.essential.items.language")}</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mt-6">{t("privacy.cookies.optional.title")}</h3>
                <p>{t("privacy.cookies.optional.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.cookies.optional.items.analytics")}</li>
                  <li>{t("privacy.cookies.optional.items.preferences")}</li>
                </ul>
                <p className="mt-2">{t("privacy.cookies.optional.withdrawal")}</p>

                <p className="mt-4">{t("privacy.cookies.browserSettings")}</p>
              </div>
            </section>

            {/* 8. Data Retention */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.dataRetention.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.dataRetention.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.dataRetention.items.account")}</li>
                  <li>{t("privacy.dataRetention.items.testData")}</li>
                  <li>{t("privacy.dataRetention.items.logs")}</li>
                  <li>{t("privacy.dataRetention.items.consent")}</li>
                  <li>{t("privacy.dataRetention.items.legal")}</li>
                </ul>
              </div>
            </section>

            {/* 9. International Transfers */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.internationalTransfers.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.internationalTransfers.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>{t("privacy.internationalTransfers.items.adequacy")}</li>
                  <li>{t("privacy.internationalTransfers.items.scc")}</li>
                  <li>{t("privacy.internationalTransfers.items.safeguards")}</li>
                </ul>
                <p className="mt-4 text-sm">{t("privacy.internationalTransfers.note")}</p>
              </div>
            </section>

            {/* 10. Your Rights */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.rights.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.rights.description")}</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>{t("privacy.rights.items.access.title")}</strong> {t("privacy.rights.items.access.description")}</li>
                  <li><strong>{t("privacy.rights.items.rectification.title")}</strong> {t("privacy.rights.items.rectification.description")}</li>
                  <li><strong>{t("privacy.rights.items.deletion.title")}</strong> {t("privacy.rights.items.deletion.description")}</li>
                  <li><strong>{t("privacy.rights.items.restriction.title")}</strong> {t("privacy.rights.items.restriction.description")}</li>
                  <li><strong>{t("privacy.rights.items.portability.title")}</strong> {t("privacy.rights.items.portability.description")}</li>
                  <li><strong>{t("privacy.rights.items.objection.title")}</strong> {t("privacy.rights.items.objection.description")}</li>
                  <li><strong>{t("privacy.rights.items.withdraw.title")}</strong> {t("privacy.rights.items.withdraw.description")}</li>
                </ul>
                <p className="mt-4">
                  {t("privacy.rights.contact")} <a href="mailto:support@testassets.io" className="text-primary hover:underline">support@testassets.io</a>
                </p>
                <p className="text-sm">{t("privacy.rights.responseTime")}</p>
              </div>
            </section>

            {/* 11. Complaint */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.complaint.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.complaint.description")}</p>
              </div>
            </section>

            {/* 12. Data Security */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.security.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.security.p1")}</p>
                <p>{t("privacy.security.p2")}</p>
              </div>
            </section>

            {/* 13. Changes */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">{t("privacy.changes.title")}</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>{t("privacy.changes.p1")}</p>
                <p>{t("privacy.changes.p2")}</p>
              </div>
            </section>

            <div className="bg-muted border border-border rounded-lg p-6 text-center">
              <p className="text-sm text-muted-foreground">{t("privacy.footer.questions")}</p>
              <p className="text-xs text-muted-foreground mt-2">{t("privacy.footer.lastUpdated")}</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      </div>
    </SidebarProvider>
  );
};

export default PrivacyPolicy;
